import { AppBar, Toolbar, Typography, IconButton, Badge } from '@mui/material';
import ArrowBack from '@mui/icons-material/ArrowBack';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAlertCount, markAllAlertsRead } from '@/features/alerts';
import { useParentApp } from '@/shared/hooks/useParentApp';

function usePageTitle(): string {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  if (pathname.startsWith('/search')) return t('search.title');
  if (
    pathname.startsWith('/publish') ||
    (pathname.startsWith('/offers/') && pathname.endsWith('/edit'))
  ) {
    return t('publish.title');
  }
  if (pathname.startsWith('/favorites'))
    return t('favorites.title', { defaultValue: 'Mes Favoris' });
  return t('app.title', { defaultValue: 'Bourse aux Matériaux' });
}

export function GlobalHeader() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isInsideIframe, navigateToRockease } = useParentApp();
  const { count: alertCount, refetch } = useAlertCount();
  const title = usePageTitle();
  const isHome = pathname === '/';

  const handleBack = () => {
    if (isHome) {
      navigateToRockease();
    } else {
      navigate('/');
    }
  };

  const handleNotificationClick = async () => {
    if (alertCount > 0) {
      markAllAlertsRead().catch(() => undefined);
      await refetch();
    }
    navigate('/alerts');
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        backgroundColor: 'primary.main',
        borderRadius: '0 0 16px 16px',
        zIndex: 999,
        paddingTop: 'env(safe-area-inset-top)',
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between', minHeight: 64 }}>
        {isHome && !isInsideIframe ? (
          <div style={{ width: 44 }} />
        ) : (
          <IconButton
            color="inherit"
            onClick={handleBack}
            aria-label={isHome ? 'Retour à ROCKEASE' : 'Retour à l\'accueil'}
            data-testid={isHome ? 'global-header-back-to-parent' : 'global-header-back-to-home'}
            sx={{ minWidth: 44, minHeight: 44 }}
          >
            <ArrowBack />
          </IconButton>
        )}

        <Typography
          variant="h6"
          component="div"
          sx={{ color: 'white', fontWeight: 500, flex: 1, textAlign: 'center' }}
        >
          {title}
        </Typography>

        <IconButton
          color="inherit"
          onClick={handleNotificationClick}
          aria-label="Aller aux alertes"
          data-testid="global-header-bell-icon"
          sx={{ minWidth: 44, minHeight: 44 }}
        >
          <Badge
            badgeContent={alertCount}
            color="secondary"
            invisible={alertCount === 0}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            aria-label={alertCount > 0 ? `${alertCount} nouvelles alertes` : 'Alertes'}
          >
            <NotificationsIcon sx={{ color: 'white' }} />
          </Badge>
        </IconButton>
      </Toolbar>
    </AppBar>
  );
}
